import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import API from '../../../context/API';
import type { Order, OrdersHistoryResponse, OrdersByTypeResponse } from '@/components/Orders/types';

const ORDERS_ENDPOINT = '/orders';

export type OrderTabType = 'pending' | 'preparing' | 'ready' | 'completed' | 'history';

export interface DateRangePayload {
  startDate: string;
  endDate: string;
}

export interface FetchHistoryParams {
  startDate: string;
  endDate: string;
  page: number;
  limit: number;
  storeId?: string;
}

export interface FetchHistoryFilterParams {
  filter: string;
  page: number;
  limit: number;
  storeId?: string;
}

export interface FetchOrdersByTypeParams {
  status: OrderTabType;
  startDate?: string;
  endDate?: string;
  storeId?: string;
}

export type TabCounts = Record<OrderTabType, number>;

interface OrdersState {
  orders: Order[];
  totalPages: number;
  totalCount: number;
  selectedTab: OrderTabType;
  selectedDateRange: DateRangePayload | null;
  selectedFilter: string;
  isFilter: boolean;
  expandedOrderId: string | null;
  tabCounts: TabCounts;
  loading: boolean;
  error: string | null;
}

const initialTabCounts: TabCounts = {
  pending: 0,
  preparing: 0,
  ready: 0,
  completed: 0,
  history: 0,
};

const initialState: OrdersState = {
  orders: [],
  totalPages: 1,
  totalCount: 0,
  selectedTab: 'pending',
  selectedDateRange: null,
  selectedFilter: '',
  isFilter: false,
  expandedOrderId: null,
  tabCounts: initialTabCounts,
  loading: false,
  error: null,
};

export const fetchOrdersByType = createAsyncThunk<
  Order[],
  FetchOrdersByTypeParams,
  { rejectValue: string }
>(
  'orders/fetchByType',
  async (params, { rejectWithValue }) => {
    try {
      const requestParams: any = {
        type: params.status,
      };

      if (params.startDate) {
        requestParams.startDate = params.startDate;
      }
      if (params.endDate) {
        requestParams.endDate = params.endDate;
      }
      if (params.storeId) {
        requestParams.storeId = params.storeId;
      }

      const response = await API.get<OrdersByTypeResponse>(ORDERS_ENDPOINT, {
        params: requestParams,
      });
      if (response.data?.success && response.data?.data) {
        return Array.isArray(response.data.data) ? response.data.data : [];
      }
      return rejectWithValue('Invalid response format');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch orders'
      );
    }
  }
);

export const fetchOrdersHistory = createAsyncThunk<
  { orders: Order[]; totalPages: number; totalCount?: number },
  FetchHistoryParams | FetchHistoryFilterParams,
  { rejectValue: string }
>(
  'orders/fetchHistory',
  async (params, { rejectWithValue }) => {
    try {
      const historyParams = params as FetchHistoryParams;
      const requestParams: any = {
        type: 'history',
        page: params.page,
        limit: params.limit,
      };

      if (historyParams.startDate) {
        requestParams.startDate = historyParams.startDate;
      }
      if (historyParams.endDate) {
        requestParams.endDate = historyParams.endDate;
      }
      if (params.storeId) {
        requestParams.storeId = params.storeId;
      }

      const response = await API.get<OrdersHistoryResponse>(ORDERS_ENDPOINT, {
        params: requestParams,
      });
      if (response.data?.success && response.data?.data) {
        let orders: any[] = [];
        let totalPages = 1;
        let totalCount: number | undefined;
        const data = response.data.data;
        if (typeof data === 'object' && 'orders' in data) {
          orders = data.orders || [];
          totalPages = data.totalPages ?? 1;
          // Extract totalCount if available from API response
          totalCount = (data as any).totalCount;
        }
        else if (Array.isArray(data)) {
          orders = data;
        }
        return { orders, totalPages, totalCount };
      }
      return rejectWithValue('Invalid response format');
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch orders history'
      );
    }
  }
);

const ordersSlice = createSlice({
  name: 'orders',
  initialState,
  reducers: {
    setSelectedTab: (state, action: PayloadAction<OrderTabType>) => {
      state.selectedTab = action.payload;
    },
    setSelectedDateRange: (state, action: PayloadAction<DateRangePayload | null>) => {
      state.selectedDateRange = action.payload;
    },
    setSelectedFilter: (state, action: PayloadAction<string>) => {
      state.selectedFilter = action.payload;
    },
    setIsFilter: (state, action: PayloadAction<boolean>) => {
      state.isFilter = action.payload;
    },
    setExpandedOrderId: (state, action: PayloadAction<string | null>) => {
      state.expandedOrderId = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOrdersByType.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrdersByType.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload;
        // Extract status from the params object
        const status = (action.meta.arg as FetchOrdersByTypeParams).status;
        state.tabCounts[status] = action.payload.length;
        state.error = null;
      })
      .addCase(fetchOrdersByType.rejected, (state, action) => {
        state.loading = false;
        state.orders = [];
        state.error = action.payload ?? 'Failed to fetch orders';
      })
      .addCase(fetchOrdersHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOrdersHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = action.payload.orders;
        state.totalPages = action.payload.totalPages;
        state.totalCount = action.payload.totalCount ?? 0;
        state.tabCounts.history = state.totalCount;
        state.error = null;
      })
      .addCase(fetchOrdersHistory.rejected, (state, action) => {
        state.loading = false;
        state.orders = [];
        state.error = action.payload ?? 'Failed to fetch orders history';
      });
  },
});

export const {
  setSelectedTab,
  setSelectedDateRange,
  setSelectedFilter,
  setIsFilter,
  setExpandedOrderId,
  clearError,
} = ordersSlice.actions;

export default ordersSlice.reducer;
